import React, { createContext, useContext, useState, useEffect } from 'react';
import { convertCurrency } from '../Uitiles/ConvterCurrency';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('INR');
  const [userLocation, setUserLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar', icon: '💵', countries: ['US', 'CA'] },
    { code: 'EUR', symbol: '€', name: 'Euro', icon: '💶', countries: ['DE', 'FR', 'IT', 'ES', 'NL'] },
    { code: 'GBP', symbol: '£', name: 'British Pound', icon: '💷', countries: ['GB'] },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee', icon: '🇮🇳', countries: ['IN'] },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen', icon: '🇯🇵', countries: ['JP'] },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', icon: '🇦🇺', countries: ['AU'] },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', icon: '🇨🇦', countries: ['CA'] },
  ];

  // Detect user location and set appropriate currency
  useEffect(() => {
    detectUserLocation();
  }, []);

  const detectUserLocation = async () => {
    try {
      // Try to get location from IP geolocation
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data.country_code) {
        setUserLocation(data);
        
        // Auto-select currency based on location
        const locationCurrency = currencies.find(currency => 
          currency.countries.includes(data.country_code)
        );
        
        if (locationCurrency && locationCurrency.code !== 'INR') {
          const savedCurrency = localStorage.getItem('selectedCurrency');
          if (!savedCurrency) {
            setSelectedCurrency(locationCurrency.code);
            localStorage.setItem('selectedCurrency', locationCurrency.code);
          }
        }
      }
    } catch (error) {
      console.log('Location detection failed, using default currency');
    }
  };

  const formatPrice = (amount, currency = selectedCurrency) => {
    const currencyData = currencies.find(c => c.code === currency);
    if (!currencyData) return `${amount}`;
    
    return `${currencyData.symbol}${amount.toFixed(2)}`;
  };

  const convertPrice = async (amount, fromCurrency = 'INR', toCurrency = selectedCurrency) => {
    if (fromCurrency === toCurrency) return amount;
    
    try {
      setIsLoading(true);
      const converted = await convertCurrency(amount, toCurrency, fromCurrency);
      return converted;
    } catch (error) {
      console.error('Currency conversion failed:', error);
      return amount; // Fallback to original amount
    } finally {
      setIsLoading(false);
    }
  };

  const updateCurrency = (newCurrency) => {
    setSelectedCurrency(newCurrency);
    localStorage.setItem('selectedCurrency', newCurrency);
  };

  useEffect(() => {
    const savedCurrency = localStorage.getItem('selectedCurrency');
    if (savedCurrency) {
      setSelectedCurrency(savedCurrency);
    }
  }, []);

  const value = {
    selectedCurrency,
    currencies,
    userLocation,
    formatPrice,
    convertPrice,
    updateCurrency,
    isLoading
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
